import {decrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import type {ConnectionMutationVariables} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type UseMutationResult, useQueryClient} from '@github-ui/react-query'
import {deleteIssueMutation as deleteIssueMutationQueryId} from '@github-ui/server-persisted-queries'
import type {DeleteIssueMutationMutation} from '@github-ui/server-persisted-queries/types/deleteIssueMutation'

import type {IssueDeletionConnection} from '../components/OptionConfig'
import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'

export function useDeleteIssueMutation({
  owner,
  repository,
  number,
  issueId,
  issueDeletionConnection,
  onCompleted,
  onError,
}: {
  owner: string
  repository: string
  number: number
  issueId: string
  issueDeletionConnection?: IssueDeletionConnection
  onCompleted?: (issueId: string) => void | Promise<void>
  onError?: (error: Error) => void
}): UseMutationResult<string, Error, ConnectionMutationVariables> {
  const ownerId = issueDeletionConnection?.ownerId ?? issueId
  const connectionPath = issueDeletionConnection?.connectionPath ?? []
  const queryClient = useQueryClient()

  return useConnectionMutation<string>({
    ownerId,
    connectionPath,
    mutationFn: async () => {
      const data = await submitDeleteIssueMutation({owner, repository, number})
      const deletedIssueId = data.deleteIssue?.issue?.id
      if (!deletedIssueId) throw new Error('Issue deletion failed')
      return deletedIssueId
    },
    onSuccess: async deletedIssueId => {
      if (issueDeletionConnection) {
        await settleConfirmedOperation({
          queryClient,
          targets: issueDeletionConnection.targets,
          ownerId: issueDeletionConnection.ownerId,
          operation: {
            type: 'removeFromConnection',
            connectionPath: issueDeletionConnection.connectionPath,
            nodeId: deletedIssueId,
          },
        })
      }
      await onCompleted?.(deletedIssueId)
      decrementIssuesTabCounter()
    },
    onError,
  })
}

export function submitDeleteIssueMutation({
  owner,
  repository,
  number,
}: {
  owner: string
  repository: string
  number: number
}) {
  return submitIssueLifecycleMutation<DeleteIssueMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}.json`,
    method: 'DELETE',
    queryId: deleteIssueMutationQueryId,
    errorMessage: 'Issue deletion failed',
    isSuccess: data => data.deleteIssue?.issue != null,
  })
}
